import React, { useCallback, useEffect, useMemo, useState } from "react";
import Layout from "../components/Layout";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Clock, ShieldAlert, X } from "lucide-react";
import { toast } from "sonner";

const empty = { reason: "", minutes: 30 };

export default function Ephemeral() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [now, setNow] = useState(Date.now());

  const load = useCallback(async () => {
    try { const { data } = await api.get("/ephemeral/sessions"); setItems(data); }
    catch (err) { console.error("ephemeral load failed", err); }
  }, []);
  useEffect(() => { load(); const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, [load]);

  const elevate = async () => {
    if (!form.reason.trim()) { toast.error("Reason required"); return; }
    setBusy(true);
    try { await api.post("/ephemeral/elevate", form); toast.success("Elevated"); setForm(empty); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const revoke = async (id) => {
    try { await api.post(`/ephemeral/sessions/${id}/revoke`); toast.success("Revoked"); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const active = useMemo(() => items.filter((s) => !s.revoked && new Date(s.granted_until).getTime() > now), [items, now]);
  const past = useMemo(() => items.filter((s) => s.revoked || new Date(s.granted_until).getTime() <= now), [items, now]);

  return (
    <Layout title="Ephemeral Mode" subtitle="TIME-BOXED ELEVATION">
      <div className="border border-amber-500/30 bg-amber-500/5 rounded-sm p-3 mb-6 text-xs font-mono text-amber-300">
        ⚠ Advisory only — server-side enforcement is pending. UI surfaces elevation status; AdminUser extractor does not yet consult this collection.
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="border border-zinc-800 rounded-sm bg-zinc-900/60 p-5">
          <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-cyan-400 mb-3">// REQUEST ELEVATION</div>
          <div className="space-y-3">
            <div><L>Reason</L><Input data-testid="eph-reason-input" value={form.reason} placeholder="Investigate ticket #1234" onChange={(e) => setForm({ ...form, reason: e.target.value })} className="bg-zinc-900 border-zinc-800 rounded-sm" /></div>
            <div><L>Minutes (1–60)</L><Input data-testid="eph-min-input" type="number" min="1" max="60" value={form.minutes} onChange={(e) => setForm({ ...form, minutes: parseInt(e.target.value || "0", 10) })} className="bg-zinc-900 border-zinc-800 rounded-sm font-mono w-32" /></div>
            <Button data-testid="eph-elevate-btn" onClick={elevate} disabled={busy} className="bg-cyan-500 hover:bg-cyan-400 text-zinc-950 rounded-sm gap-2 w-full">
              <ShieldAlert className="w-4 h-4" /> {busy ? "..." : "REQUEST ELEVATION"}
            </Button>
          </div>
        </div>

        <div className="border border-zinc-800 rounded-sm bg-zinc-900/60 p-5">
          <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-cyan-400 mb-3">// ACTIVE</div>
          {active.length === 0 && <div className="text-zinc-500 font-mono text-sm">no active elevation.</div>}
          <div className="space-y-2" data-testid="eph-active">
            {active.map((s) => {
              const remaining = Math.max(0, Math.floor((new Date(s.granted_until).getTime() - now) / 1000));
              return (
                <div key={s.session_id} className="border border-cyan-500/40 bg-cyan-500/5 rounded-sm p-3">
                  <div className="flex items-center justify-between">
                    <div className="font-mono text-xs text-cyan-300 flex items-center gap-2"><Clock className="w-3.5 h-3.5" /> {fmt(remaining)} remaining</div>
                    <Button size="sm" variant="ghost" data-testid={`eph-revoke-${s.session_id}`} onClick={() => revoke(s.session_id)} className="gap-1.5 text-red-400 hover:text-red-300"><X className="w-3.5 h-3.5" />Revoke</Button>
                  </div>
                  <div className="text-xs text-zinc-300 mt-2">{s.reason}</div>
                  <div className="text-[10px] font-mono text-zinc-500 mt-1">{s.user_email} · until {s.granted_until?.slice(11, 19)}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="mt-6">
        <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-zinc-500 mb-3">// HISTORY</div>
        <div className="border border-zinc-800 rounded-sm bg-zinc-900/40 divide-y divide-zinc-800 max-h-96 overflow-y-auto" data-testid="eph-history">
          {past.length === 0 && <div className="text-zinc-500 font-mono text-sm p-4">no past sessions.</div>}
          {past.map((s) => (
            <div key={s.session_id} className="px-4 py-2 grid grid-cols-12 gap-2 font-mono text-xs">
              <div className="col-span-3 text-zinc-500 whitespace-normal break-words">{s.granted_at?.slice(0, 19)}</div>
              <div className="col-span-2 text-zinc-400 whitespace-normal break-words">{s.user_email}</div>
              <div className="col-span-1 text-zinc-500">{s.minutes}m</div>
              <div className="col-span-5 text-zinc-300 whitespace-normal break-words">{s.reason}</div>
              <div className={"col-span-1 uppercase " + (s.revoked ? "text-amber-400" : "text-zinc-500")}>{s.revoked ? "revoked" : "expired"}</div>
            </div>
          ))}
        </div>
      </div>
    </Layout>
  );
}

function fmt(secs) {
  const m = Math.floor(secs / 60), s = secs % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}
function L({ children }) { return <Label className="text-[10px] font-mono uppercase tracking-widest text-zinc-400">{children}</Label>; }
